import {
  BundleModelFactory,
  SimulateFailure,
  BundleDataType,
} from './bundle.db'

import { z } from 'zod'
import { LocalizedErrorDto } from './localizedErrorMessages'
import { vector2, vector3 } from './utils'
import { ModelUtils } from './utils'
import type { Timestamp as AdminTimestamp } from 'firebase-admin/firestore'
import type { Timestamp } from 'firebase-admin/firestore'

export class BundleDTOModelFactory {
  public BundleCreateObjectScanDto
  public BundleCreatePrimeJobDto
  public BundleCreatePackagedCollectionDto
  public BundleCreateDto
  public BundleLocalizedErrorDto
  public BundleResponseDto
  public BundleFileMetadataDto
  public BundleUploadManifestDto
  public BundlePrimeJobMessageDto
  public BundleRevertDto
  public BundleArchiveDto
  public BundleUnarchiveDto
  public BundlePosterDto
  public BundlePayloadScanDto
  private utils

  constructor(timestamp: typeof Timestamp | typeof AdminTimestamp) {
    this.utils = new ModelUtils(timestamp)
    const {
      BundleMonocleVideoDBModel,
      BundleOwnerEditableDBModel,
      BundleHolder,
    } = new BundleModelFactory(timestamp)

    this.BundleCreateObjectScanDto = z.object({
      /**
       * Min bound of vertices in scan (meters)
       * [x, y, z](readonly)
       */
      min_coordinate: vector3,
      /**
       * Max bound of vertices in scan (meters)
       * [x, y, z](readonly)
       */
      max_coordinate: vector3,
    })

    this.BundleCreatePrimeJobDto = z.object({
      /**
       * Set flag for automatic stencil masking
       */
      auto_stencil: z.boolean(),
    })

    this.BundleCreatePackagedCollectionDto = z.object({
      /**
       * Source collection to package.
       */
      collection_id: z.string().min(1),
      /**
       * Distribution key this package is assigned to.
       */
      distribution_key_id: z.string().min(1),
    })

    this.BundleCreateDto = z.object({
      data_type: z.nativeEnum(BundleDataType),
      created: this.utils.date,
      poster_jpg_base64: z.string(),
      /**
       * Poster mask for the bundle
       */
      poster_mask_png_base64: z.string().optional(),
      /**
       * The client ID is client side ID, that gives a client context for when it comes back
       */
      client_id: z.string().optional(),
      /**
       * The thread ID is the related set of created bundles that share common source data.
       */
      thread_id: z.string().optional(),
      /**
       * Optional flag to simulate standard bundle processing errors
       */
      simulate_failure: z.nativeEnum(SimulateFailure).optional(),
      /**
       * Metadata details that the owner may modify.
       */
      owner_editable: BundleOwnerEditableDBModel,
      /**
       * Monocle scanner specific metadata
       */
      object_scan: this.BundleCreateObjectScanDto.optional(),
      /**
       * Monocle Video specific metadata
       */
      monocle_video: BundleMonocleVideoDBModel.optional(),
      /**
       * Monocle Prime Job specific metadata
       */
      prime_job: this.BundleCreatePrimeJobDto.optional(),
      /**
       * Packaged Collection specific create metadata.
       */
      packaged_collection: this.BundleCreatePackagedCollectionDto.optional(),
    })

    this.BundleLocalizedErrorDto = z.object({
      bundle_id: z.string(),
      error: LocalizedErrorDto.optional(),
    })

    this.BundleResponseDto = BundleHolder

    this.BundleFileMetadataDto = z.object({
      /// Sets the metadata.created field with this datetime,
      /// And if no modified field, then also sets the
      /// `metadata.object_scan.edit_modified`
      /// and `owner_editable.modified field`
      created: this.utils.date.optional(),

      /// Sets the metadata.object_scan.edit_modified and owner_editable.modified field with this datetime
      modified: this.utils.date.optional(),

      /// Valid keypaths are in BundleKeyPathsAllowed, where this file should attach to in the Bundle
      keypath: z.string().optional(),

      /// Indicate the file originates from a server upload,
      /// so let it through onFinalize without quota checks.
      server_upload: z.any().transform(this.utils.booleanCoercer).optional(),
    })

    this.BundleUploadManifestDto = z.object({
      poster: z
        .object({
          poster_jpg_base64: z.string(),
          poster_mask_png_base64: z.string().optional(),
        })
        .optional(),
      object_scan: z
        .object({
          created: this.BundleFileMetadataDto.shape.created,
          modified: this.BundleFileMetadataDto.shape.modified,
          camera_euler_angles: vector3.optional(),
          camera_pan: vector3.optional(),
        })
        .optional(),
      files: z.array(
        z.object({
          keypath: z.string(),
          filename: z.string(),
          created: this.BundleFileMetadataDto.shape.created,
          modified: this.BundleFileMetadataDto.shape.modified,
        }),
      ),
    })

    this.BundlePrimeJobMessageDto = z.object({
      user_id: z.string(),
      bundle_id: z.string(),
      filepath: z.string(),
    })

    this.BundleRevertDto = z.object({
      bundle_id: z.string(),
      poster_jpg_base64: z.string(),
      poster_mask_png_base64: z.string(),
      camera_euler_angles: vector3.default([0, 0, 0]),
      camera_pan: vector3.optional(),
    })

    this.BundleArchiveDto = z.object({
      bundle_id: z.string(),
    })

    this.BundleUnarchiveDto = z.object({
      owner: z.string(),
      bundle_id: z.string(),
    })

    this.BundlePosterDto = z.object({
      bundle_id: z.string(),
      poster_jpg_base64: z.string(),
      poster_mask_png_base64: z.string().optional(),
      camera_euler_angles: vector3.default([0, 0, 0]),
      camera_pan: vector3.optional(),
    })

    this.BundlePayloadScanDto = z.object({
      data_type: z.nativeEnum(BundleDataType),
      id: z.string(),
      created: this.utils.date,
      auto_stencil: z.boolean(),
      bundle_id: z.string(),
      owner_editable: BundleOwnerEditableDBModel,
    })
  }
}
